import { useState } from 'react'
import type { PlanVersionSummary } from '@/lib/apiTypes'
import { formatDateTime, relativeTime } from '@/lib/format'
import { ActionChips } from '@/features/plans/components/ActionChips'
import { StalenessChip } from '@/features/plans/components/StalenessChip'

// ── Plan ref group (latest card + collapsed history) ────────────────────────────
export function PlanRefGroup({
  group, currentSerial, selectedVersionId, onSelect,
}: {
  group: { ref: string | null; versions: PlanVersionSummary[] }
  currentSerial: number | null | undefined
  selectedVersionId: string | null
  onSelect: (id: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const latest = group.versions[0]
  const older = group.versions.slice(1)
  const isSelected = latest.id === selectedVersionId

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, marginBottom: 10, background: isSelected ? 'var(--bg3)' : 'var(--bg2)' }}>
      <button
        onClick={() => onSelect(latest.id)}
        style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer', padding: '12px 14px' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: '0.72rem', color: 'var(--accent)' }}>
            {group.ref ?? 'default (no ref)'}
          </span>
          <span className="badge badge-accent" style={{ fontSize: '0.55rem' }}>latest</span>
          <ActionChips v={latest} />
          {latest.drift_count > 0 && <span className="badge badge-amber" style={{ fontSize: '0.58rem' }}>{latest.drift_count} drift</span>}
          <StalenessChip priorSerial={latest.prior_serial} currentSerial={currentSerial} />
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: '0.62rem', color: 'var(--text3)' }} title={formatDateTime(latest.created_at)}>{relativeTime(latest.created_at)}</span>
        </div>
      </button>
      {older.length > 0 && (
        <div style={{ borderTop: '1px solid var(--border)' }}>
          <button
            onClick={() => setExpanded((v) => !v)}
            style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer', padding: '6px 14px', fontFamily: 'var(--mono)', fontSize: '0.62rem', color: 'var(--text3)' }}
          >
            {expanded ? '▾' : '▸'} {older.length} superseded plan{older.length !== 1 ? 's' : ''}
          </button>
          {expanded && (
            <div style={{ padding: '0 14px 10px' }}>
              {older.map((v) => (
                <button
                  key={v.id}
                  onClick={() => onSelect(v.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', background: v.id === selectedVersionId ? 'var(--bg3)' : 'transparent', border: 'none', cursor: 'pointer', padding: '5px 6px', borderRadius: 4, opacity: 0.6, flexWrap: 'wrap' }}
                >
                  <ActionChips v={v} size="0.6rem" />
                  <StalenessChip priorSerial={v.prior_serial} currentSerial={currentSerial} />
                  <div style={{ flex: 1 }} />
                  <span style={{ fontSize: '0.6rem', color: 'var(--text3)' }} title={formatDateTime(v.created_at)}>{relativeTime(v.created_at)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
